/**
 * Which of the live-breaks widget's rows show, and the player's streams (docs/live-breaks.md,
 * "Storefront"; boxed's `resolveVisibleWidgetSlots` and `usePinnedStreamRow`). The rows
 * themselves are built by the design system's `liveBreakWidget.ts` (sponsored live breaks,
 * promoted streams, the Return rows, the picker), which the theme preview runs too; what is the
 * app's is when each shows:
 *
 * - on a live break's page no row shows (the player alone stays), so the widget never covers the
 *   page's chat;
 * - the stream slot fills only while the player is closed and the shopper hasn't hidden it: the
 *   pinned live break, else a promoted stream they picked while it is still live, else the best
 *   sponsored live break not already a Return row, else the best promoted stream;
 * - the pinned live break is the one last opened in the player, shown while it is open (Draining or
 *   not, its host present or not), built from the pin until its first read.
 */
import { type LiveBreak, liveBreakStorefrontPath, parseTwitchLogin } from "@loot-os/api-contract";
import { liveBreakHostName } from "@loot-os/design-system/domain/liveBreaks/liveBreakState";
import {
  hasStreamChoices,
  type LiveBreakWidgetHosting,
  type LiveBreakWidgetPickerSections,
  type LiveBreakWidgetQueue,
  type LiveBreakWidgetStream,
  liveBreakWidgetStream,
  resolveWidgetStream,
  widgetPickerSections,
} from "@loot-os/design-system/domain/liveBreaks/liveBreakWidget";

import type { WatchableStream } from "./pip.js";

/** A live break's page, where a widget row leads. */
export const liveBreakHref = (liveBreakId: string) => liveBreakStorefrontPath(liveBreakId);

/** The ids of the live break pages, as the router names them (`/live-breaks/$liveBreakId`). */
const LIVE_BREAK_PAGE = /^\/live-breaks\/([^/]+)\/?$/;
/** The pages beside a live break's under `/live-breaks/`: never a live break's id. */
const STATIC_SEGMENTS = new Set(["host"]);

/** The live break whose page `pathname` is, or null on every other page. */
export function liveBreakIdFromPath(pathname: string): string | null {
  const segment = LIVE_BREAK_PAGE.exec(pathname)?.[1];
  if (!segment || STATIC_SEGMENTS.has(segment)) return null;
  try {
    return decodeURIComponent(segment);
  } catch {
    return null;
  }
}

/**
 * A live break as the player plays it (its toolbar's and its directory card's PiP), under its
 * host's name, or the store's for a house live break; null when it streams no channel.
 */
export function liveBreakStream(
  liveBreak: Pick<LiveBreak, "id" | "host" | "twitchChannel" | "twitchStatus">,
  houseName: string,
): WatchableStream | null {
  const channel = liveBreak.twitchChannel ? parseTwitchLogin(liveBreak.twitchChannel) : null;
  if (!channel) return null;
  return {
    channel,
    displayName: liveBreakHostName(liveBreak, houseName),
    avatarUrl: liveBreak.twitchStatus?.avatarUrl ?? null,
    liveBreakId: liveBreak.id,
  };
}

/** A widget row's stream as the player plays it. */
export function widgetStreamToWatchable(stream: LiveBreakWidgetStream): WatchableStream {
  return {
    channel: stream.channel,
    displayName: stream.name,
    avatarUrl: stream.avatarSrc,
    liveBreakId: stream.liveBreak?.id ?? null,
  };
}

/**
 * A stream the player remembers (the pin, a recently watched live break) as a widget row, its
 * phase unknown until its live break is read.
 */
export function watchableToWidgetStream(stream: WatchableStream): LiveBreakWidgetStream {
  return {
    key: stream.liveBreakId ?? `promoted:${stream.channel}`,
    channel: stream.channel,
    name: stream.displayName,
    avatarSrc: stream.avatarUrl,
    priority: 0,
    liveBreak: stream.liveBreakId
      ? { id: stream.liveBreakId, href: liveBreakHref(stream.liveBreakId), phase: null }
      : null,
  };
}

/** The pinned live break's row, and whether the pin is gone (the caller drops it). */
export interface PinnedWidgetStream {
  stream: LiveBreakWidgetStream | null;
  gone: boolean;
}

/**
 * The pinned live break's row. Until its first read (or after a failed one) it is built from the
 * pin, with no phase yet. It is gone once the live break closed, can't be found, or streams
 * another channel; while it is open it shows, ahead of every sponsored row, unless the shopper
 * hosts it, whose hosting Return row stands in for it.
 */
export function pinnedWidgetStream({
  pinned,
  read,
  houseName,
  avatarSrc,
}: {
  pinned: WatchableStream | null;
  /** The live break's read: its detail, `missing` on a 404, or undefined while unread or failed. */
  read: (LiveBreak & { isViewerHost: boolean }) | "missing" | undefined;
  houseName: string;
  /** The channel's latest Twitch avatar, when read. */
  avatarSrc?: string | null;
}): PinnedWidgetStream {
  if (pinned === null) return { stream: null, gone: false };
  if (pinned.liveBreakId === null || read === "missing") return { stream: null, gone: true };
  if (read === undefined) return { stream: watchableToWidgetStream(pinned), gone: false };
  const channel = read.twitchChannel ? parseTwitchLogin(read.twitchChannel) : null;
  if (read.status === "closed" || channel !== pinned.channel) return { stream: null, gone: true };
  if (read.isViewerHost) return { stream: null, gone: false };
  const stream = liveBreakWidgetStream(read, {
    href: liveBreakHref(read.id),
    houseName,
    priority: 0,
  });
  return {
    stream: stream && { ...stream, avatarSrc: avatarSrc ?? stream.avatarSrc ?? pinned.avatarUrl },
    gone: false,
  };
}

/** Everything the widget's rows are resolved from. */
export interface WidgetRowsInput {
  /** The live break whose page is showing, if any. */
  activeLiveBreakId: string | null;
  /** The player is open. */
  pipOpen: boolean;
  streamRowHidden: boolean;
  hosting: LiveBreakWidgetHosting | null;
  queue: LiveBreakWidgetQueue | null;
  pinned: LiveBreakWidgetStream | null;
  /** The sponsored live breaks' rows, by priority (`sponsoredLiveBreakStreams`). */
  sponsored: readonly LiveBreakWidgetStream[];
  /** The promoted streams, by priority (`promotedWidgetStreams`). */
  promoted: readonly LiveBreakWidgetStream[];
  /** The promoted stream the shopper picked, by channel. */
  pickedChannel: string | null;
  /** The live breaks watched lately and still open, newest first, as rows. */
  recent: readonly LiveBreakWidgetStream[];
}

/** The rows the widget is given, each null when it doesn't show. */
export interface WidgetRows {
  stream: LiveBreakWidgetStream | null;
  hosting: LiveBreakWidgetHosting | null;
  /** Null while the hosting row shows: it replaces the queue row. */
  queue: LiveBreakWidgetQueue | null;
  /** The stream picker's groups, null with nothing to choose between. */
  picker: LiveBreakWidgetPickerSections | null;
}

export const NO_WIDGET_ROWS: WidgetRows = {
  stream: null,
  hosting: null,
  queue: null,
  picker: null,
};

/**
 * Which rows show. None on a live break's page. The hosting Return row replaces the queue row; a
 * sponsored live break already shown as a Return row gets no stream row of its own, and leaves the
 * picker (a pinned one still shows beside its Return row).
 */
export function resolveWidgetRows(input: WidgetRowsInput): WidgetRows {
  if (input.activeLiveBreakId !== null) return NO_WIDGET_ROWS;
  const hosting = input.hosting;
  const queue = hosting ? null : input.queue;
  const shownReturn = hosting?.liveBreakId ?? queue?.liveBreakId ?? null;
  const sponsored = input.sponsored.filter((stream) => stream.liveBreak?.id !== shownReturn);
  const open = !input.pipOpen && !input.streamRowHidden;
  const stream = open
    ? resolveWidgetStream({
        pinned: input.pinned,
        pickedChannel: input.pickedChannel,
        sponsored,
        promoted: input.promoted,
      })
    : null;
  const sections = widgetPickerSections({
    sponsored,
    promoted: input.promoted,
    recent: input.recent,
  });
  return { stream, hosting, queue, picker: hasStreamChoices(sections) ? sections : null };
}
